import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import DatePicker from 'react-datepicker';
import PropTypes from 'prop-types';
import 'react-datepicker/dist/react-datepicker.css';
import './styles.scss';

import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { IoCloseOutline } from 'react-icons/io5';

import Dropdown from './Dropdown';

dayjs.extend(customParseFormat);

const range = (start, end) => {
  return Array(end - start + 1)
    .fill()
    .map((_, idx) => start + idx);
};

const getRecentYears = (nYears, showNextYears, nNextYears) => {
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - nYears;
  const endYear = showNextYears ? currentYear + nNextYears : currentYear;
  return range(startYear, endYear).map((e) => e.toString());
};

const CalendarPicker = ({
  selected,
  onDateChange,
  onDateClear,
  showReminder,
  showNextYears = false,
  nPastYears = 5,
  nNextYears = 5,
  hasClear = true,
  entityId,
  reminders = [],
  updateReminder,
  createReminder,
  deleteReminder,
  closeCalendar,
  datePreference,
}) => {
  const [newDate, setNewDate] = useState(() => {
    if (selected) {
      if (datePreference) return dayjs(selected, datePreference).toDate();
      return dayjs(selected).toDate();
    }
    return dayjs().toDate();
  });

  const [isSelected, toggleSelected] = useState(!!selected);
  const years = getRecentYears(nPastYears, showNextYears, nNextYears);
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const reminderOptions = [
    'On Date',
    'Day(s) Before',
    'Week(s) Before',
    'Month(s) Before',
  ];
  const TYPES = {
    0: { label: 'On Date', key: 'days' },
    1: { label: 'Day(s) Before', key: 'days' },
    2: { label: 'Week(s) Before', key: 'weeks' },
    3: { label: 'Month(s) Before', key: 'months' },
  };

  const numbers = range(1, 15);
  const [isDateSelected, setDateSelect] = useState(false);

  const calendarRef = useRef();

  useEffect(() => {
    if (isDateSelected && newDate && onDateChange) {
      onDateChange(newDate);
      setDateSelect(false);
    }
  }, [newDate, isDateSelected, onDateChange]);

  const onClearDate = () => {
    setDateSelect(false);
    setNewDate(new Date());
    toggleSelected(false);
    if (onDateClear) onDateClear();
  };

  const addReminder = () => {
    createReminder({
      variables: {
        taskId: entityId,
        input: {
          spec: {
            delta: {
              days: 0,
            },
          },
        },
      },
    });
  };

  // #TODO: Make variables names generalized and more semantic.
  const updateReminderValue = (taskId, definitionId, value, key) => {
    let newValue = value;
    // If switching from "On Date" to another value, force value to be 1.
    if (newValue === 0 && key !== 'days') {
      newValue = 1;
    }
    updateReminder({
      variables: {
        taskId,
        definitionId,
        input: {
          spec: {
            delta: {
              [key]: newValue,
            },
          },
        },
      },
    });
  };


  const removeReminder = (taskId, definitionId) => {
    deleteReminder({
      variables: {
        taskId,
        definitionId,
      },
    });
  };

  return (
    <div
      className="calendar_container"
      ref={calendarRef}
    >
      <DatePicker
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          // decreaseMonth,
          // increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div className="custom_header">
            <div
              onClick={() => {
                const prevMonth = date.getMonth() - 1;
                // const d = new Date(newDate.setMonth(prevMonth, 1));
                // setNewDate(d);
                // toggleSelected(true);
                changeMonth(prevMonth);
              }}
              disabled={prevMonthButtonDisabled}
              className="custom_header__prev"
            >
              <i className="fa fa-angle-left custom_header__arrow-left" />
            </div>
            <Dropdown
              options={months}
              value={months[date.getMonth()]}
              nShowItems={12}
              onChangeValue={(monthTextValue) => {
                const monthValue = months.indexOf(monthTextValue);
                if (monthValue < 0) changeMonth(0);
                changeMonth(monthValue);
              }}
              className="select_wrapper--month"
            />
            <Dropdown
              options={years}
              value={date.getFullYear().toString()}
              nShowItems={6}
              onChangeValue={(yeartextValue) => {
                const yearValue = Number(yeartextValue);
                changeYear(yearValue);
              }}
              className="select_wrapper--year"
            />
            <div
              onClick={() => {
                const nextMonth = date.getMonth() + 1;
                // setNewDate(new Date(newDate.getFullYear(), nextMonth, 1));
                // toggleSelected(true);
                changeMonth(nextMonth);
              }}
              disabled={nextMonthButtonDisabled}
              className="custom_header__next"
            >
              <i className="fa fa-angle-right custom_header__arrow-right" />
            </div>
          </div>
        )}
        renderDayContents={(day, date) => {
          return (
            <section className="custom_datepicker__day">
              {date.getDate()}
            </section>
          );
        }}
        selected={isSelected ? newDate : null}
        inline
        onChange={(date) => {
          setNewDate(date);
          toggleSelected(true);
          setDateSelect(true);
          if (reminders.length > 0) {
            reminders.forEach(reminder => {
              const { taskId, definitionId, delta } = reminder;
              const type = delta.days ? delta.days > 0 ? 1 : 0 : delta.weeks ? 2 : delta.months ? 3 : 0;
              const numericValue = delta.days ?? delta.weeks ?? delta.months;
              updateReminderValue(taskId, definitionId, numericValue, TYPES[type].key);
            });
          }
        }}
        onClickOutside={(e) => {
          if (calendarRef.current.contains(e.target)) return;
          closeCalendar(e);
        }}
      />
      {!!selected && hasClear && (
        <div className="function-bar">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClearDate();
            }}
          >
            <IoCloseOutline className="clear_date"
              size={20}
            />
            Clear date
            {/* <i className="fa fa-close clear_date"></i> Clear date */}
          </button>
        </div>
      )}
      {selected && showReminder && (
        <div className="reminder-bar">
          <div>
            <button className="add-reminder"
              onClick={addReminder}
            >
              Add A Reminder +
            </button>
          </div>
          {reminders.length > 0 &&
            reminders.map((reminder, index) => {
              const { taskId, definitionId, delta } = reminder;
              const type = delta.days ? delta.days > 0 ? 1 : 0 : delta.weeks ? 2 : delta.months ? 3 : 0;
              const numericValue = delta.days ?? delta.weeks ?? delta.months;
              const updateValue = numericValue < 1 ? 1 : numericValue;
              return (
                <div key={index}
                  className="reminder"
                >
                  {(delta.days > 0 || delta.weeks || delta.months) && (
                    <Dropdown
                      options={numbers}
                      value={numericValue}
                      nShowItems={10}
                      onChangeValue={(newValue) => {
                        updateReminderValue(taskId, definitionId, newValue, TYPES[type].key);
                      }}
                      style={{
                        width: '50px',
                        fontSize: '12px',
                        textAlign: 'center',
                      }}
                    />
                  )}
                  <Dropdown
                    options={reminderOptions}
                    value={TYPES[type].label}
                    nShowItems={4}
                    onChangeValue={(newValue) => {
                      const key = Object.keys(TYPES).find(t => TYPES[t].label === newValue);
                      if (key) {
                        updateReminderValue(taskId, definitionId, key === '0' ? 0 : updateValue, TYPES[key].key);
                      }
                    }}
                    style={{ width: '200px', textTransform: 'uppercase' }}
                  />
                  <button
                    onClick={() => {
                      removeReminder(taskId, definitionId);
                    }}
                  >
                    <HighlightOffIcon style={{ fontSize: '20px' }} />
                  </button>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

CalendarPicker.propTypes = {
  selected: PropTypes.string,
  onDateChange: PropTypes.func,
  onDateClear: PropTypes.func,
  showReminder: PropTypes.bool,
  showNextYears: PropTypes.bool,
  nPastYears: PropTypes.number,
  nNextYears: PropTypes.number,
  hasClear: PropTypes.bool,
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reminders: PropTypes.array,
  updateReminder: PropTypes.func,
  createReminder: PropTypes.func,
  deleteReminder: PropTypes.func,
  closeCalendar: PropTypes.func,
};

export default CalendarPicker;
